import React, { useState, useEffect, useRef, RefObject, MutableRefObject } from 'react'
import PreviewAndData from 'plugin-assets/shared/widget/multi-channel/editor/preview-and-data'
import styled from 'styled-components'
import SideBarCsb from '../components/Sidebar'
import { redirectPathSvgPath } from '../utils/utils'
import { EmptyData } from '@intelie/ui'
import { setViewBoxOnSvg, debouncedGetDefaultQuery } from '../utils/utils'
import i18n from 'live/services/i18n'

export const FileEditor = (props: FullChannelsWidgetEditorProps): React.ReactNode => {
    const {
        data,
        widgetConfigActions,
        widgetConfigStore,
        widgetDataActions,
        widgetDataStore,
        meta,
        renderPreview
    } = props
    const { manualQuery, svgString, eventType, assets, file } = data?.jsonConfig
    const selectedAsset = assets?.[0]?.asset
    const [eventTypeLocal, setEventTypeLocal] = useState(eventType)
    const isEmpty = !data || !svgString
    const containerRef: MutableRefObject<HTMLDivElement | undefined> = useRef()
    const [defaultQuery, setDefaultQuery] = useState('')

    useEffect(() => {
        widgetConfigActions.changeConfig('eventType', eventTypeLocal)
        debouncedGetDefaultQuery(eventTypeLocal, (query: string) => setDefaultQuery(query))
    }, [eventTypeLocal])

    useEffect(() => {
        setViewBoxOnSvg(containerRef)
    }, [containerRef])

    const onApplyManualQuery = (manualQueryLocal: string) => {
        widgetConfigActions.changeConfig('manualQuery', manualQueryLocal !== defaultQuery && manualQueryLocal)
    }

    const onSelectFile = ({ svg, file }: { svg: string; file: { name: string; wellName: string } }) => {
        if (svg) {
            const newFileData = redirectPathSvgPath(svg)
            widgetConfigActions.changeConfig('svgString', newFileData)
            widgetConfigActions.changeConfig('file', file)
        } else {
            widgetConfigActions.changeConfig('svgString', null)
            widgetConfigActions.changeConfig('file', null)
        }
    }

    return (
        <EditorContainer className="editor-container default-editor">
            <PreviewContainer ref={containerRef}>
                <SideBarCsb
                    defaultQuery={defaultQuery}
                    manualQuery={manualQuery}
                    onApplyManualQuery={onApplyManualQuery}
                    onSelectFile={onSelectFile}
                    file={file}
                    setEventType={setEventTypeLocal}
                    eventType={eventTypeLocal}
                />
                <PreviewAndData
                    widgetConfigStore={widgetConfigStore}
                    widgetConfigActions={widgetConfigActions}
                    widgetDataStore={widgetDataStore}
                    widgetDataActions={widgetDataActions}
                    widget={data}
                    renderPreview={renderPreview}
                    isEmpty={isEmpty}
                    emptyComponent={
                        <ContentEmpty>
                            <EmptyData
                                title={i18n('No schematic selected')}
                                subtitle={i18n('Import a schematic to display')}
                            />
                        </ContentEmpty>
                    }
                    isAssetSelected={selectedAsset}
                    meta={meta}
                />
            </PreviewContainer>
        </EditorContainer>
    )
}

const Editor = (
    widgetConfigStore: Record<string, any>,
    widgetConfigActions: Record<string, any>,
    widgetDataStore: Record<string, any>,
    widgetDataActions: Record<string, any>,
    meta: Array<any>
): ((props: ChannelsWidgetEditorProps) => React.ReactNode) => {
    return function EditorWrapper(props: ChannelsWidgetEditorProps): React.ReactNode {
        return (
            <FileEditor
                widgetConfigStore={widgetConfigStore}
                widgetDataActions={widgetDataActions}
                widgetConfigActions={widgetConfigActions}
                widgetDataStore={widgetDataStore}
                meta={meta}
                {...props}
            />
        )
    }
}

const EditorContainer = styled.div`
    display: flex;
    flex: 1 1 auto;
`
const PreviewContainer = styled.div`
    display: flex;
    flex: 1 1 auto;
    .widget-content {
        display: flex;
        justify-content: center;
        align-items: center;
    }
`
const ContentEmpty = styled.div`
    height:100%;
    > div {
        display: flex;
        justify-content: center;
        align-items: center;
        height:100%;
        width:100%;
    }
`
export default Editor
